import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
} from "@mui/material";
import API from "../../utils/api";
import { getOrCreateConversation } from "../../api/conversations";
import { tSuccess, tError } from "../../utils/toast";
import { getUserInitials } from "../../utils/userInitials";
import { formatPhoneDisplay } from "../../utils/formatPhone";

/**
 * New Message Modal
 * Search leads by name/phone, optionally prefill message, create/find conversation.
 * Wired to: GET /api/leads/?search=... and POST /api/conversations/get_or_create/
 */
export default function NewMessageModal({
  open,
  onClose,
  onCreated,
}) {
  const [search, setSearch] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [prefill, setPrefill] = useState("");
  const [creating, setCreating] = useState(false);

  const getLeadName = (l) =>
    [l.first_name, l.last_name].filter(Boolean).join(" ").trim() || "Unknown";
  const getLeadPhone = (l) => formatPhoneDisplay(l.phone);
  const getLeadAddress = (l) => l.address || "";

  const fetchLeads = useCallback(async () => {
    if (!search.trim()) {
      setLeads([]);
      return;
    }
    try {
      setLoading(true);
      const res = await API.get("/leads/", {
        params: { search: search.trim(), page_size: 20 },
      });
      const list = Array.isArray(res?.data) ? res.data : res?.data?.results || [];
      setLeads(list);
    } catch (err) {
      console.error(err);
      tError("Failed to search leads");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchLeads, 300);
    return () => clearTimeout(timer);
  }, [fetchLeads]);

  const handleConfirm = async () => {
    if (!selectedLead?.id) return;

    try {
      setCreating(true);
      const res = await getOrCreateConversation(selectedLead.id);
      const conversation = res?.data;
      if (!conversation?.id) {
        tError("Failed to create or find conversation");
        return;
      }
      tSuccess("Conversation opened");
      onClose();
      setSelectedLead(null);
      setSearch("");
      setPrefill("");
      if (onCreated) {
        onCreated(conversation, prefill.trim() || null);
      }
    } catch (err) {
      console.error(err);
      tError(err?.response?.data?.error || "Failed to create conversation");
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedLead(null);
    setSearch("");
    setPrefill("");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          zIndex: 2000,
          borderRadius: 3,
          backgroundImage: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0))"
              : "linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0))",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(2, 6, 23, 0.87)"
              : "rgba(255, 255, 255, 0.87)",
          border: (theme) =>
            theme.palette.mode === "dark"
              ? "1px solid rgba(255,255,255,0.12)"
              : "1px solid rgba(0,0,0,0.08)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 25px 70px rgba(0,0,0,0.8)"
              : "0 25px 70px rgba(0,0,0,0.25)",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
        New Message
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Search for a lead by name, phone, or address to start a conversation.
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="Search leads by name or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
          {loading && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              Searching…
            </Typography>
          )}
          {!loading && search.trim() && leads.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              No leads found. Try a different search.
            </Typography>
          )}
          {!loading && leads.length > 0 && (
            <List dense>
              {leads.map((lead) => (
                <ListItemButton
                  key={lead.id}
                  selected={selectedLead?.id === lead.id}
                  onClick={() => setSelectedLead(lead)}
                  sx={{ borderRadius: 2, mb: 0.5 }}
                >
                  <ListItemAvatar>
                    <Avatar>
                      {getUserInitials(
                        {
                          first_name: lead.first_name,
                          last_name: lead.last_name,
                          full_name: getLeadName(lead),
                        },
                        "U"
                      )}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography fontWeight={700}>
                        {getLeadName(lead)}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        {getLeadPhone(lead) && (
                          <Typography variant="caption" display="block">
                            {getLeadPhone(lead)}
                          </Typography>
                        )}
                        {getLeadAddress(lead) && (
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {getLeadAddress(lead)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        {selectedLead && (
          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder="Optional: Add a message to prefill…"
            value={prefill}
            onChange={(e) => setPrefill(e.target.value)}
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedLead || creating}
          onClick={handleConfirm}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {creating ? "Opening…" : "Start Conversation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
